type TimesheetEntryUser = {
  displayName?: string | null;
  email?: string | null;
  contacts?: Array<{ contactValue?: string | null; isPrimary?: boolean; contactType?: string }>;
};

/** Prisma Decimal and similar API values may arrive as string or number. */
export function parseApiDecimal(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

export function formatDecimalFixed(value: unknown, decimals = 2): string | null {
  const n = parseApiDecimal(value);
  return n == null ? null : n.toFixed(decimals);
}

export function durationHoursFromTimes(
  startTime?: string | null,
  stopTime?: string | null
): number | null {
  if (!startTime || !stopTime) {
    return null;
  }
  const ms = new Date(stopTime).getTime() - new Date(startTime).getTime();
  if (!Number.isFinite(ms)) {
    return null;
  }
  return ms / (1000 * 60 * 60);
}

export function timesheetEntryHoursLabel(
  entry: {
    hours?: unknown;
    startTime?: string | null;
    stopTime?: string | null;
  },
  options?: { decimals?: number; fallback?: string }
): string {
  const decimals = options?.decimals ?? 2;
  const fallback = options?.fallback ?? 'N/A';
  const fromHours = formatDecimalFixed(entry.hours, decimals);
  if (fromHours != null) {
    return fromHours;
  }
  const duration = durationHoursFromTimes(entry.startTime, entry.stopTime);
  if (duration != null) {
    return duration.toFixed(decimals);
  }
  return fallback;
}

export function timesheetEntryDateKey(entry: {
  startTime?: string | null;
  stopTime?: string | null;
}): string | null {
  const raw = entry.startTime ?? entry.stopTime;
  return raw ?? null;
}

export function timesheetEntryUserLabel(entry: { user?: TimesheetEntryUser | null }): string {
  const user = entry.user;
  if (!user) {
    return 'N/A';
  }
  if (user.displayName) {
    return user.displayName;
  }
  const contacts = user.contacts ?? [];
  const primaryEmail = contacts.find((c) => c.isPrimary && c.contactType === 'email');
  if (primaryEmail?.contactValue) {
    return primaryEmail.contactValue;
  }
  const anyEmail = contacts.find((c) => c.contactType === 'email');
  if (anyEmail?.contactValue) {
    return anyEmail.contactValue;
  }
  if (user.email) {
    return user.email;
  }
  return 'N/A';
}
